import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, userApi } from '@/services/api';
import { useUserStore } from '@/stores/userStore';

interface User {
  username: string;
  email?: string;
  account_type?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  logout: () => void;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    console.log('Checking auth state...');
    try {
      // Call backend to validate and refresh token
      const response = await authApi.checkLogin();

      if (response && response.username) {
        // Fetch account info to get account_type
        let accountType = response.account_type;
        let email = response.email;

        try {
          const accountInfo = await userApi.fetchAccountInfo();
          if (accountInfo && !accountInfo.error) {
            accountType = accountInfo.account_type || accountType;
            email = accountInfo.email || email;
          }
        } catch (error) {
          console.error('[AuthContext] Failed to fetch account info:', error);
        }

        console.log('[AuthContext] final account_type:', accountType);

        setUser({
          username: response.username,
          email: email || localStorage.getItem('email') || undefined,
          account_type: accountType || localStorage.getItem('account_type') || undefined
        });
        console.log('User authenticated:', response.username);

        // Load user settings from backend after successful authentication
        useUserStore.getState().loadUserSettingsFromBackend();
      } else {
        // No valid session
        setUser(null);
        console.log('User not authenticated');
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const logout = () => {
    console.log('Logging out...');
    setUser(null);
    localStorage.removeItem('id_token');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('account_type');

    // Clear user settings from store on logout
    useUserStore.getState().clearUser();
  };

  const value: AuthContextType = {
    user,
    loading,
    setUser,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
